// src/components/home/GeoButton.js
// Bouton de recherche par proximité - renvoie vers la carte - NE PAS MODIFIER

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { useTheme } from '../../hooks/useTheme';
import LocationPermissionModal from '../shared/LocationPermissionModal';
import ROUTES from '../../constants/routes';

const GeoButton = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [showPermissionGuide, setShowPermissionGuide] = useState(false);

  const handleClick = async () => {
    setLoading(true);

    // App native (Android/iOS) → plugin Capacitor
    if (Capacitor.isNativePlatform()) {
      try {
        const permission = await Geolocation.requestPermissions();
        if (permission.location !== 'granted' && permission.coarseLocation !== 'granted') {
          setLoading(false);
          setShowPermissionGuide(true);
          return;
        }
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 8000,
        });
        const { latitude, longitude } = position.coords;
        setLoading(false);
        navigate(`${ROUTES.MAP_SEARCH}?lat=${latitude}&lng=${longitude}`);
      } catch (error) {
        setLoading(false);
        setShowPermissionGuide(true);
      }
      return;
    }

    // Navigateur web classique → API standard
    if (!navigator.geolocation) {
      setLoading(false);
      setShowPermissionGuide(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLoading(false);
        navigate(`${ROUTES.MAP_SEARCH}?lat=${latitude}&lng=${longitude}`);
      },
      (error) => {
        setLoading(false);
        if (error.code === 1) setShowPermissionGuide(true);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const styles = {
    container: { padding: '0 16px', marginBottom: '16px' },
    button: {
      display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '14px 16px',
      backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '12px',
      cursor: 'pointer', transition: 'all 200ms ease-in-out', minHeight: '52px', textAlign: 'left',
    },
    iconCircle: {
      width: '32px', height: '32px', borderRadius: '50%', backgroundColor: theme.goldSoft,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', flexShrink: 0,
    },
    textGroup: { display: 'flex', flexDirection: 'column', gap: '1px' },
    label: { fontSize: '14px', fontWeight: '700', color: theme.textPrimary },
    subLabel: { fontSize: '12px', color: theme.textSecondary },
    arrow: { marginLeft: 'auto', color: theme.gold, fontSize: '16px' },
  };

  return (
    <div style={styles.container}>
      <button
        style={styles.button}
        onClick={handleClick}
        disabled={loading}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = theme.goldBorder)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = theme.border)}
      >
        <div style={styles.iconCircle}>{loading ? '⏳' : '🧭'}</div>
        <div style={styles.textGroup}>
          <span style={styles.label}>{loading ? t('common.loading') : t('home.useLocation')}</span>
          <span style={styles.subLabel}>Voir les annonces proches sur la carte</span>
        </div>
        <span style={styles.arrow}>→</span>
      </button>

      <LocationPermissionModal open={showPermissionGuide} onClose={() => setShowPermissionGuide(false)} />
    </div>
  );
};

export default GeoButton;